import { z } from 'zod';
import { mobileContractScreenIdSchema } from '../screens';
import { replayMetadataSchema } from '../replay-privacy';

/**
 * A single-screen surface document: a versioned envelope around exactly one
 * renderable spec, with optional host metadata. Distinct from a flow definition
 * — it has no step keys, navigation, progress, or validation gate. `.strict()`
 * rejects flow-only fields so a surface can never be modeled as a one-screen
 * flow (ADR-046 Rule 9).
 */
export const surfaceDocumentSchema = z
  .object({
    schema_version: z.string(),
    surface_id: z.string(),
    screen_id: mobileContractScreenIdSchema,
    locale: z.string().optional(),
    spec: z
      .object({
        root: z.string(),
        // Element records are deliberately opaque at the envelope layer — their
        // shape (type, props, children, and the optional per-element `fallback`
        // naming a sibling element id that older clients substitute for an
        // unknown kind) is validated by the design-system catalog layer, which
        // owns the element vocabulary.
        elements: z.record(z.string(), z.unknown()),
      })
      .strict(),
    analytics: z.record(z.string(), z.string()).optional(),
    replay: replayMetadataSchema.optional(),
    fallback: z
      .object({ type: z.literal('host') })
      .strict()
      .optional(),
  })
  .strict()
  .superRefine((document, ctx) => {
    for (const elementKey of Object.keys(document.replay?.elements ?? {})) {
      if (!(elementKey in document.spec.elements)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['replay', 'elements', elementKey],
          message: 'replay metadata must reference a defined element',
        });
      }
    }
  });

export type SurfaceDocument = z.infer<typeof surfaceDocumentSchema>;
